import { useEffect, useState } from "react";
import { api, type ProviderInfo, type SetupState } from "../api";
import { Select } from "./Select";
import { fehlerText } from "./verwaltung";

/**
 * Auswahl von Anbieter und Modell. Wird an zwei Stellen gebraucht: im
 * Einrichtungsassistenten und spaeter unter Einstellungen — deshalb eigenstaendig.
 */
interface Props {
  current: SetupState["llm"];
  onSaved: () => void;
  /** Im Assistenten ueberspringbar, in den Einstellungen nicht. */
  onSkip?: () => void;
  /** In den Einstellungen: Bearbeiten abbrechen. */
  onCancel?: () => void;
}

export function ModelAccess({ current, onSaved, onSkip, onCancel }: Props) {
  const configured = current.configured;
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [provider, setProvider] = useState(current.provider || "langdock");
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState<"eu" | "us">((current.region as "eu" | "us") || "eu");
  const [baseUrl, setBaseUrl] = useState(current.baseUrl || "");
  const [apiVersion, setApiVersion] = useState(current.apiVersion || "2024-10-21");
  const [models, setModels] = useState<{ id: string; display_name?: string }[]>([]);
  const [model, setModel] = useState(current.model || "");
  const [fastModel, setFastModel] = useState(current.fastModel || "");
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [providerFehler, setProviderFehler] = useState<string | null>(null);

  useEffect(() => {
    api.providers().then((r) => setProviders(r.providers)).catch((e) => setProviderFehler(fehlerText(e)));
  }, []);

  const gewaehlt = providers.find((p) => p.id === provider);
  const reset = () => { setModels([]); setModel(""); setHinweis(null); setError(null); };

  const check = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.testLlmKey({
        provider, apiKey, region, baseUrl: baseUrl || undefined,
        apiVersion: provider === "azure" ? apiVersion.trim() || undefined : undefined,
      });
      setModels(result.models);
      setHinweis(result.hinweis ?? null);
      const bevorzugt =
        result.models.find((m) => /sonnet|mistral-large|^gpt-5-mini$|^gpt-4\.1$|^gpt-4o$/i.test(m.id)) ?? result.models[0];
      if (bevorzugt) setModel(bevorzugt.id);
      // Azure liefert keine Liste — dann muss der Name von Hand kommen.
      if (result.models.length === 0) setModel("");
    } catch (err) {
      setError(`Zugang nicht bestätigt: ${fehlerText(err)}`);
      setModels([]);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveLlm({
        provider, apiKey, region, model,
        fastModel: fastModel || null,
        baseUrl: baseUrl || null,
        ...(provider === "azure" && apiVersion.trim() ? { apiVersion: apiVersion.trim() } : {}),
      });
      onSaved();
    } catch (err) {
      setError(`Nicht gespeichert: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const geprueft = models.length > 0 || hinweis !== null;

  return (
    <div>
      <h2>Modellzugang</h2>
      <p className="lede">
        Der Agent schickt Betreff und einen gekürzten Textauszug jeder Mail zur Einordnung an den
        gewählten Anbieter. Wo das verarbeitet wird, entscheidest du hier.
      </p>

      {providerFehler && <p className="error" role="alert">Die Anbieterliste ließ sich nicht laden: {providerFehler}</p>}
      {providers.length === 0 && !providerFehler && <div className="v-laden" data-klein="true" role="status">Lade Anbieter …</div>}
      <div className="provider-list" role="radiogroup" aria-label="Anbieter">
        {providers.map((p) => (
          <label key={p.id} className="choice" data-selected={provider === p.id}>
            <input type="radio" name="modell-anbieter" checked={provider === p.id} onChange={() => { setProvider(p.id); reset(); }} />
            <span>
              <b>
                {p.name}
                <span className={`dsgvo dsgvo-${p.dsgvo}`}>
                  {p.dsgvo === "eu" ? "EU" : p.dsgvo === "selbst" ? "selbst bestimmt" : "USA"}
                </span>
              </b>
              <span className="hint">{p.hosting} — {p.note}</span>
            </span>
          </label>
        ))}
      </div>

      {gewaehlt?.needsBaseUrl && (
        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="ma-url">Adresse des Endpunkts</label>
          <input
            id="ma-url"
            className="input mono"
            value={baseUrl}
            onChange={(e) => { setBaseUrl(e.target.value); reset(); }}
            placeholder={provider === "azure" ? "https://meine-ressource.openai.azure.com" : "https://mein-server:8000/v1"}
          />
          <p className="hint">
            {provider === "azure"
              ? "Nur der Anfang der Adresse, ohne /openai/deployments. Achte auf eine EU-Region beim Anlegen der Ressource."
              : "Bis einschließlich /v1. Alles, was das OpenAI-Format spricht, funktioniert hier."}
          </p>
        </div>
      )}

      {provider === "azure" && (
        <div className="field" style={{ marginTop: 4 }}>
          <label htmlFor="ma-version">API-Version</label>
          <input
            id="ma-version"
            className="input mono"
            value={apiVersion}
            onChange={(e) => { setApiVersion(e.target.value); reset(); }}
            placeholder="2024-10-21"
          />
          <p className="hint">
            Steht im Azure-Portal bei der Bereitstellung. Neuere Modelle brauchen oft eine neuere Version,
            etwa „2025-01-01-preview".
          </p>
        </div>
      )}

      <div className="v-raster" style={{ gridTemplateColumns: provider === "langdock" ? "minmax(0, 3fr) minmax(0, 1fr)" : "minmax(0, 1fr)", marginTop: 16 }}>
        <div className="field">
          <label htmlFor="ma-key">{provider === "azure" ? "API-Schlüssel der Ressource" : "API-Schlüssel"}</label>
          <input
            id="ma-key"
            className="input mono"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); reset(); }}
            placeholder={provider === "langdock" ? "ld-…" : provider === "anthropic" ? "sk-ant-…" : provider === "openai" ? "sk-…" : "…"}
          />
          {configured && <p className="hint">Der gespeicherte Schlüssel wird nie angezeigt. Zum Ändern von Anbieter oder Modell bitte neu eingeben.</p>}
        </div>
        {provider === "langdock" && (
          <div className="field">
            <label>Region</label>
            <Select
              value={region}
              onChange={(v) => { setRegion(v as "eu" | "us"); reset(); }}
              options={[{ value: "eu", label: "EU" }, { value: "us", label: "US" }]}
              aria-label="Region"
            />
          </div>
        )}
      </div>

      {!geprueft ? (
        <button className="btn" disabled={busy || apiKey.trim().length < 4 || (gewaehlt?.needsBaseUrl && !baseUrl)} onClick={check}>
          {busy ? "Prüfe …" : "Zugang prüfen"}
        </button>
      ) : (
        <>
          {models.length > 0 && <div className="ok-line">Zugang bestätigt — {models.length} {models.length === 1 ? "Modell" : "Modelle"} verfügbar.</div>}
          {hinweis && <p className="hint">{hinweis}</p>}

          <div className="v-raster" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Modell für Antworten</label>
              {models.length > 0 ? (
                <Select
                  value={model}
                  onChange={setModel}
                  options={models.map((m) => ({ value: m.id, label: m.display_name ?? m.id }))}
                  aria-label="Modell"
                />
              ) : (
                <input className="input mono" aria-label="Modell für Antworten" value={model} onChange={(e) => setModel(e.target.value)}
                  placeholder={provider === "azure" ? "name-deiner-bereitstellung" : "modell-id"} />
              )}
            </div>
            <div className="field">
              <label>Günstigeres Modell fürs Sortieren</label>
              {models.length > 0 ? (
                <Select
                  value={fastModel}
                  onChange={setFastModel}
                  options={[
                    { value: "", label: "dasselbe verwenden" },
                    ...models.map((m) => ({ value: m.id, label: m.display_name ?? m.id })),
                  ]}
                  aria-label="Günstigeres Modell"
                />
              ) : (
                <input className="input mono" aria-label="Günstigeres Modell fürs Sortieren" value={fastModel} onChange={(e) => setFastModel(e.target.value)}
                  placeholder="leer = dasselbe" />
              )}
            </div>
          </div>
          <p className="hint">
            Das kleinere Modell übernimmt die Masse — Einordnen und Belege auslesen. Das größere schreibt
            nur die Antwortentwürfe. Das drückt die Kosten deutlich.
          </p>
        </>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <div className="setup-foot">
        <button className="btn primary" disabled={busy || !model.trim() || !apiKey.trim() || (gewaehlt?.needsBaseUrl && !baseUrl)} onClick={save}
          title={!apiKey.trim() ? "Bitte zuerst den API-Schlüssel eingeben" : undefined}>
          {busy && geprueft ? "Speichert …" : onSkip ? "Speichern und weiter" : "Speichern"}
        </button>
        {onCancel && <button className="btn quiet" onClick={onCancel} disabled={busy}>Abbrechen</button>}
        {onSkip && (
          <button className="btn quiet" onClick={onSkip}>
            {configured ? "Unverändert lassen" : "Später einrichten"}
          </button>
        )}
      </div>
      {!configured && (
        <p className="hint">
          Ohne Modellzugang holt der Agent deine Mails zwar ab und bewertet sie technisch auf Spam,
          ordnet sie aber nicht ein und schreibt keine Entwürfe.
        </p>
      )}
      {configured && (
        <p className="hint">Ein Wechsel gilt sofort — ohne Neustart.</p>
      )}
    </div>
  );
}

